import { useState, useEffect, useCallback } from 'react';
import { capacidadesAPI, formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Save, Loader2, RotateCcw, Lock } from 'lucide-react';
import { toast } from 'sonner';
import QueSeConfiguraAca from '@/components/QueSeConfiguraAca';

/**
 * Con qué valores corre cada capacidad del producto en esta cápsula.
 *
 * **Los parámetros son un formulario, no un párrafo.** «Umbral 0,35» no es algo que se le
 * explique en prosa a un modelo: es un número que el motor usa. Hasta hoy vivían en tres lugares
 * que no se conocían —`kb_settings` del tenant, `kb_overrides` del agente e
 * `investigation_config`— y ninguna pantalla decía de dónde salía el que estaba rigiendo.
 *
 * **De dónde viene cada valor se muestra siempre.** Un número sin saber si lo puso el producto,
 * la organización o este agente no se puede juzgar ni corregir. Es lo que permite leer «la
 * cápsula dice 4» en vez de un 4 pelado.
 *
 * **Lo que fija el producto se ve y no se edita.** Verlo evita la pregunta; esconderlo obliga a
 * adivinar de dónde sale el límite; editarlo sería darle al cliente una perilla que no sabemos si
 * aguanta.
 */
const DE_DONDE = {
  producto: ['lo fija OmniSyS', 'text-zinc-400'],
  tenant: ['heredado de la organización', 'text-zinc-500'],
  capsula: ['decidido en esta cápsula', 'text-brand-primary'],
  agente: ['lo pisa el agente', 'text-amber-700'],
};

export default function ParametrosDeCapacidad({ tenantId, capsuleId }) {
  const [capacidades, setCapacidades] = useState([]);
  const [borrador, setBorrador] = useState({});
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState('');

  const cargar = useCallback(async () => {
    if (!tenantId || !capsuleId) return;
    setCargando(true);
    try {
      const { data } = await capacidadesAPI.parametros(tenantId, capsuleId);
      setCapacidades(data || []);
      setBorrador({});
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudieron leer los parámetros');
    } finally {
      setCargando(false);
    }
  }, [tenantId, capsuleId]);

  useEffect(() => { cargar(); }, [cargar]);

  const valorMostrado = (cap, p) => {
    const propio = borrador[cap.capacidad_id]?.[p.nombre];
    if (propio !== undefined) return propio;
    if (p.valor === null || p.valor === undefined) return '';
    return Array.isArray(p.valor) ? p.valor.join(', ') : String(p.valor);
  };

  const cambiar = (capId, nombre, valor) =>
    setBorrador((prev) => ({ ...prev, [capId]: { ...(prev[capId] || {}), [nombre]: valor } }));

  const sucia = (capId) => Object.keys(borrador[capId] || {}).length > 0;

  /** Del texto del formulario al tipo que el parámetro declara. El backend valida igual. */
  const aSuTipo = (p, crudo) => {
    const texto = String(crudo ?? '').trim();
    if (!texto) return null;              // vacío = volver a heredar
    if (p.tipo === 'entero') return Number.parseInt(texto, 10);
    if (p.tipo === 'decimal') return Number.parseFloat(texto.replace(',', '.'));
    if (p.tipo === 'booleano') return texto === 'true' || texto === 'sí' || texto === 'si';
    if (p.tipo === 'lista') return texto.split(',').map((x) => x.trim()).filter(Boolean);
    return texto;
  };

  const guardar = async (cap) => {
    setGuardando(cap.capacidad_id);
    try {
      const valores = {};
      for (const [nombre, crudo] of Object.entries(borrador[cap.capacidad_id] || {})) {
        const p = cap.parametros.find((x) => x.nombre === nombre);
        if (p) valores[nombre] = aSuTipo(p, crudo);
      }
      const { data } = await capacidadesAPI.guardarParametros(cap.capacidad_id, {
        tenant_id: tenantId, capsule_id: capsuleId, valores,
      });
      if (!data.ok) { toast.error(data.errores.join(' · ')); return; }
      toast.success(`${cap.nombre}: parámetros guardados`);
      await cargar();
    } catch (err) {
      toast.error(formatApiError(err) || 'No se pudo guardar');
    } finally {
      setGuardando('');
    }
  };

  if (cargando) {
    return (
      <p className="text-xs text-zinc-500 flex items-center gap-2 py-6 justify-center">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Leyendo los parámetros…
      </p>
    );
  }

  return (
    <div className="space-y-3" data-testid="parametros-de-capacidad">
      <QueSeConfiguraAca
        testId="ayuda-parametros"
        queEs="Los números con los que trabaja cada cosa que el producto sabe hacer: cuántos resultados trae una búsqueda, qué tan parecido tiene que ser algo para contar, cuánto puede pesar un archivo."
        paraQue="Son valores que el motor usa y que no se pueden explicar en prosa a un modelo. «Umbral 0,35» no es una instrucción: es un número. Antes vivían en tres lugares que no se conocían y ninguna pantalla decía cuál estaba rigiendo."
        aQueAfecta={<>A cómo trabajan <b>los agentes de esta cápsula</b>. Subir los resultados de una búsqueda le da más material al agente y le cuesta más contexto; bajar el umbral trae más cosas y más ruido.</>}
        ejemplo={<>Hay <b>cuatro niveles y gana el más específico</b>: lo que trae el producto, lo que decide la organización, lo que decide esta cápsula, y lo que pisa un agente puntual. Cada valor dice de dónde sale. <b>Vaciar el campo vuelve a heredar</b> el de arriba, que es cómo se deshace un cambio.</>}
        ojo={<>Lo que fija el producto se ve y no se edita: son topes que no sabemos si aguantan si se tocan. Y si un valor dice que <i>todavía sale del almacén viejo</i>, es una copia que conviene mover acá — mientras siga ahí, cambiarlo desde esta pantalla no hace nada.</>}
      />

      {capacidades.map((cap) => (
        <Card key={cap.capacidad_id} className="rounded-sm border-brand-border">
          <CardHeader className="py-2 px-3 border-b border-brand-border">
            <CardTitle className="text-xs font-semibold">{cap.nombre}</CardTitle>
            <p className="text-[10px] text-zinc-500 mt-0.5">{cap.descripcion}</p>
          </CardHeader>
          <CardContent className="p-3 space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {cap.parametros.map((p) => {
                const donde = DE_DONDE[p.de_donde] || ['', 'text-zinc-400'];
                return (
                  <div key={p.nombre} className="space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-600">
                        {p.nombre.replace(/_/g, ' ')}
                      </span>
                      <span className={`text-[9px] ${donde[1]} flex items-center gap-1`}>
                        {!p.editable && <Lock className="w-2.5 h-2.5" />}
                        {donde[0]}
                      </span>
                    </div>
                    {p.tipo === 'booleano' ? (
                      <Switch
                        data-testid={`param-${cap.capacidad_id}-${p.nombre}`}
                        disabled={!p.editable}
                        checked={String(valorMostrado(cap, p)) === 'true'}
                        onCheckedChange={(v) => cambiar(cap.capacidad_id, p.nombre, String(v))}
                      />
                    ) : (
                      <Input
                        data-testid={`param-${cap.capacidad_id}-${p.nombre}`}
                        disabled={!p.editable}
                        value={valorMostrado(cap, p)}
                        onChange={(e) => cambiar(cap.capacidad_id, p.nombre, e.target.value)}
                        placeholder={p.tipo === 'lista' ? 'separados por coma' : p.tipo}
                        className="rounded-sm text-xs h-7 disabled:bg-zinc-50 disabled:text-zinc-400"
                      />
                    )}
                    <p className="text-[10px] text-zinc-400">{p.descripcion}</p>
                    {p.forma_vieja && (
                      <p className="text-[9px] text-amber-700" title="El valor todavía sale del almacén anterior">
                        todavía guardado en {p.forma_vieja}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button
                data-testid={`guardar-param-${cap.capacidad_id}`}
                onClick={() => guardar(cap)}
                disabled={guardando === cap.capacidad_id || !sucia(cap.capacidad_id)}
                className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-7"
              >
                {guardando === cap.capacidad_id
                  ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  : <Save className="w-3 h-3 mr-1" />}
                Guardar
              </Button>
              {sucia(cap.capacidad_id) && (
                <button
                  type="button"
                  onClick={() => setBorrador((prev) => ({ ...prev, [cap.capacidad_id]: {} }))}
                  className="text-[10px] text-zinc-500 hover:text-brand-primary flex items-center gap-1"
                >
                  <RotateCcw className="w-3 h-3" /> descartar
                </button>
              )}
              <Badge variant="secondary" className="text-[9px] rounded-sm ml-auto">
                {cap.parametros.filter((p) => p.de_donde === 'capsula').length} decididos acá
              </Badge>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
